"use client"

import  { useEffect, useState } from "react"
import type { Client, Material, ProjectResponse, User } from "@/utils/types"
import { ProjectService } from "@/apis/project/ProjectService"
import { ClientService } from "@/apis/client/ClientService"
import { UserService } from "@/apis/profile/UserService"
import { MaterialService } from "@/apis/material/MaterialService"
import { Link, useNavigate } from "react-router-dom"
import {Archive, BarChart, ChevronDown, ChevronUp, Minus, Pencil, Plus, Trash} from "lucide-react"
import NavbarEmployee from "@/components/NavbarEmployee"
import Navbar from "@/components/Navbar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ProgressUpdateDialog } from "@/components/ui/progress-update-dialog"

const ProjectsPage = () => {
    const [projects, setProjects] = useState<ProjectResponse[]>([])
    const [filteredProjects, setFilteredProjects] = useState<ProjectResponse[]>([])
    const [currentUser, setCurrentUser] = useState<User>()
    const [clients, setClients] = useState<Client[]>([])
    const [materials, setMaterials] = useState<Material[]>([])
    const [searchTerm, setSearchTerm] = useState("")
    const navigate = useNavigate()
    const [currentPage, setCurrentPage] = useState(1)
    const projectsPerPage = 5
    const [expandedProject, setExpandedProject] = useState<number | null>(null)
    const [isSmallScreen, setIsSmallScreen] = useState(false)
    const [progressDialogOpen, setProgressDialogOpen] = useState(false)
    const [selectedProject, setSelectedProject] = useState<ProjectResponse | null>(null)

    useEffect(() => {
        const checkScreenSize = () => {
            setIsSmallScreen(window.innerWidth < 1024) // Adjust this breakpoint as needed
        }
        checkScreenSize()
        window.addEventListener("resize", checkScreenSize)
        return () => window.removeEventListener("resize", checkScreenSize)
    }, [])

    useEffect(() => {
        loadUserInfo()
        loadClients()
        loadMaterials()
    }, [])

    useEffect(() => {
        if (currentUser && currentUser.username && currentUser.password && currentUser.id) {
            loadProjects()
        }
    }, [currentUser])

    useEffect(() => {
        filterProjects()
    }, [searchTerm, projects])

    const loadUserInfo = async () => {
        const user = await UserService.getCurrentUser()
        setCurrentUser(user)
    }

    const loadClients = async () => {
        const clientsData = await ClientService.getClients()
        setClients(clientsData)
    }

    const loadMaterials = async () => {
        const materialsData = await MaterialService.getMaterials()
        setMaterials(materialsData)
    }

    const loadProjects = async () => {
        const projectsData = await ProjectService.getUnarchivedProjects();

        // Sortează descrescător după ID
        const sorted = projectsData.sort(
          (a: { id: number }, b: { id: number }) => b.id - a.id,
        );

        setProjects(sorted);
    };


    const filterProjects = () => {
        const filtered = projects.filter(
            (project) =>
                project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getClientName(project.clientId).toLowerCase().includes(searchTerm.toLowerCase()),
        )
        setFilteredProjects(filtered)
    }

    const getClientName = (clientId: number) => {
        const client = clients.find((client) => client.id === clientId)
        return client ? client.name : "Unknown Client"
    }

    const getMaterialName = (materialId: number) => {
        const material = materials.find((material) => material.id === materialId)
        return material ? material.name : "Unknown Material"
    }

    const getMaterialCoefficient = (materialId: number) => {
        const material = materials.find((material) => material.id === materialId)
        return material ? material.coefficient : "Unknown"
    }

    const handleDelete = async (projectId: number) => {
        if (confirm("Sigur doriți să stergeți acest proiect?")) {
            await ProjectService.deleteProject(projectId)
            setProjects(projects.filter((project) => project.id !== projectId))
        }
    }

    const handleUpdate = (projectId: number) => {
        navigate("/edit-project/" + projectId)
    }

    const handleAssign = async (projectId: number) => {
        await ProjectService.addUserToProject(projectId, Number(currentUser?.id))
        loadProjects()
    }

    const handleUnassign = async (projectId: number) => {
        await ProjectService.removeUserFromProject(projectId, Number(currentUser?.id))
        loadProjects()
    }

    const handleOpenProgressDialog = (project: ProjectResponse) => {
        setSelectedProject(project)
        setProgressDialogOpen(true)
    }

    const handleCloseProgressDialog = () => {
        setProgressDialogOpen(false)
        setSelectedProject(null)
    }

    const formatHoursAndMinutes = (decimalHours: number) => {
        const hours = Math.floor(decimalHours)
        const minutes = Math.round((decimalHours - hours) * 60)

        // If total hours are less than 1 but there are minutes, show "1 orÄƒ"
        if (hours === 0 && minutes > 0) {
            return "1 oră"
        }

        // If minutes are 30 or more, add an hour
        if (minutes >= 30) {
            return hours + 1 === 1 ? `1 oră` : `${hours + 1} ore`
        } else {
            return hours === 1 ? `1 oră` : `${hours} ore`
        }
    }

    const toggleProjectExpansion = (projectId: number) => {
        setExpandedProject(expandedProject === projectId ? null : projectId)
    }

    const isUserAssignedToProject = (project: ProjectResponse) => {
        return project.users.some((user) => user.id === currentUser?.id)
    }

    const areUsersAssignedToProject = (project: ProjectResponse) => {
        return project.users.length > 0
    }

    const isLimitDateExpired = (project: ProjectResponse) => {
        return new Date(project.limitDate) < new Date()
    }

    const renderProjectCard = (project: ProjectResponse) => {
        const isExpanded = expandedProject === project.id
        const isAssigned = currentUser?.type === "ANGAJAT" && areUsersAssignedToProject(project)
        const isUserAssigned = currentUser?.type === "ANGAJAT" && isUserAssignedToProject(project)

        return (
            <Card
                key={project.id}
                className={`mb-4 ${isAssigned ? "border-2 border-[#C51321]" : ""}`}
            >
                <CardHeader
                    className={`flex cursor-pointer flex-row items-center justify-between ${currentUser?.type === "ANGAJAT" && isAssigned ? "bg-grey text-white" : "" || (currentUser?.type === "ADMIN" && isLimitDateExpired(project)) ? "bg-grey text-white" : ""}`}
                    onClick={() => toggleProjectExpansion(project.id)}
                >
                    <CardTitle className="text-lg">{project.name}</CardTitle>
                    {isExpanded ? (
                        <ChevronUp className="h-5 w-5" />
                    ) : (
                        <ChevronDown className="h-5 w-5" />
                    )}
                </CardHeader>
                {isExpanded && (
                    <CardContent>
                        <div className="space-y-2">
                            <p>
                                <strong>ID: </strong>
                                {project.id + 100}
                            </p>
                            <p>
                                <strong>Descriere:</strong> {project.description}
                            </p>
                            <p>
                                <strong>Status:</strong> {project.status}
                            </p>
                            <div className="mb-2 mt-2">
                                <strong>Progres:</strong>
                                <div className="bg-gray-200 mt-1 h-2.5 w-full rounded-full">
                                    <div
                                        className="h-2.5 rounded-full bg-[#C51321]"
                                        style={{ width: `${project.progress || 0}%` }}
                                    ></div>
                                </div>
                                <span className="mt-1 block text-right text-xs">
                      {project.progress || 0}%
                    </span>
                            </div>
                            <p>
                                <strong>Puncte:</strong> {project.points || 0}
                            </p>
                            <p>
                                <strong>Data de început:</strong> {project.startDate}
                            </p>
                            <p>
                                <strong>Data limită:</strong> {project.limitDate}
                            </p>
                            {currentUser?.type === "ADMIN" && (
                                <>
                                    <p>
                                        <strong>Client:</strong>{" "}
                                        {getClientName(project.clientId)}
                                    </p>
                                    <p>
                                        <strong>Adresă:</strong> {project.address}
                                    </p>
                                    <p>
                                        <strong>Ore lucrate:</strong>{" "}
                                        {formatHoursAndMinutes(project.hours)}
                                    </p>
                                    <p>
                                        <strong>Preț›:</strong> {project.price}
                                    </p>
                                    <p>
                                        <strong>Avans:</strong> {project.advance}
                                    </p>
                                    <p>
                                        <strong>Factură:</strong>{" "}
                                        {project.invoice ? "Da" : "Nu"}
                                    </p>
                                    <p>
                                        <strong>Cost total:</strong> {project.total}
                                    </p>
                                </>
                            )}
                            <div>
                                <strong>Materiale:</strong>
                                {project.materials.length > 0 ? (
                                    <ul className="list-inside list-disc">
                                        {project.materials.map((material, index) => (
                                            <li key={index}>
                                                {getMaterialName(material.materialId)},{" "}
                                                {getMaterialCoefficient(material.materialId)},{" "}
                                                {material.quantity}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>Niciun material</p>
                                )}
                            </div>
                            <div>
                                <strong>Angajați:</strong>
                                {project.users.length > 0 ? (
                                    <ul className="list-inside list-disc">
                                        {project.users.map((user, index) => (
                                            <li key={index}>{user.name}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>Niciun angajat</p>
                                )}
                            </div>
                            <div className="mt-4 flex flex-wrap justify-end gap-2">
                                {currentUser?.type === "ADMIN" ? (
                                    <>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdate(project.id)}
                                        >
                                            <Pencil className="mr-2 h-4 w-4" />
                                            Editează
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => handleDelete(project.id)}
                                        >
                                            <Trash className="mr-2 h-4 w-4" />
                                            Șterge
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => ProjectService.archiveProject(project.id).then(() => loadProjects())}
                                        >
                                            <Archive className="mr-2 h-4 w-4" />
                                            Arhivează
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleAssign(project.id)}
                                            disabled={isUserAssigned}
                                        >
                                            <Plus className="mr-2 h-4 w-4" />
                                            Asignează-te
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => handleUnassign(project.id)}
                                            disabled={!isUserAssigned}
                                        >
                                            <Minus className="mr-2 h-4 w-4" />
                                            Dezasignează-te
                                        </Button>
                                        {isUserAssigned && (
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenProgressDialog(project);
                                                }}
                                            >
                                                <BarChart className="mr-2 h-4 w-4" />
                                                Actualizare Progres
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                )}
            </Card>
        );
    }

    const renderProjectTable = () => (
        <div className="w-full overflow-x-auto">
            <table className="border-gray-300 min-w-full table-auto border-collapse border">
                <thead>
                <tr className="bg-gray-200">
                    {currentUser?.type === "ADMIN" ? (
                        <>
                            <th className="border-gray-300 border px-4 py-2">Acțiuni</th>
                            <th className="border-gray-300 border px-4 py-2">ID</th>
                            <th className="border-gray-300 border px-4 py-2">Nume</th>
                            <th className="border-gray-300 border px-4 py-2">
                                Descriere
                            </th>
                            <th className="border-gray-300 border px-4 py-2">Client</th>
                            <th className="border-gray-300 border px-4 py-2">Adresă</th>
                            <th className="border-gray-300 border px-4 py-2">
                                Ore lucrate
                            </th>
                            <th className="border-gray-300 border px-4 py-2">Preț</th>
                            <th className="border-gray-300 border px-4 py-2">Avans</th>
                            <th className="border-gray-300 border px-4 py-2">Factură</th>
                            <th className="border-gray-300 border px-4 py-2">Status</th>
                            <th className="border-gray-300 border px-4 py-2">Progres</th>
                            <th className="border-gray-300 border px-4 py-2">Puncte</th>
                            <th className="border-gray-300 border px-4 py-2">Creat</th>
                            <th className="border-gray-300 border px-4 py-2">
                                Termen limită
                            </th>
                            <th className="border-gray-300 border px-4 py-2">
                                Cost total
                            </th>
                            <th className="border-gray-300 border px-4 py-2">
                                Materiale
                            </th>
                            <th className="border-gray-300 border px-4 py-2">Angajați</th>
                        </>
                    ) : (
                        <>
                            <th className="border-gray-300 border px-4 py-2">Acțiuni</th>
                            <th className="border-gray-300 border px-4 py-2">ID</th>
                            <th className="border-gray-300 border px-4 py-2">Nume</th>
                            <th className="border-gray-300 border px-4 py-2">
                                Descriere
                            </th>
                            <th className="border-gray-300 border px-4 py-2">Status</th>
                            <th className="border-gray-300 border px-4 py-2">Progres</th>
                            <th className="border-gray-300 border px-4 py-2">Puncte</th>
                            <th className="border-gray-300 border px-4 py-2">
                                Data Început
                            </th>
                            <th className="border-gray-300 border px-4 py-2">
                                Data limită
                            </th>
                            <th className="border-gray-300 border px-4 py-2">
                                Materiale
                            </th>
                            <th className="border-gray-300 border px-4 py-2">Angajați</th>
                        </>
                    )}
                </tr>
                </thead>
                <tbody>
                {filteredProjects
                    .slice(
                        (currentPage - 1) * projectsPerPage,
                        currentPage * projectsPerPage,
                    )
                    .map((project) => (
                        <tr
                            key={project.id}
                            className={
                                currentUser?.type === "ANGAJAT" &&
                                areUsersAssignedToProject(project)
                                    ? "bg-grey text-white"
                                    : "" ||
                                    (currentUser?.type === "ADMIN" &&
                                        isLimitDateExpired(project))
                                        ? "bg-grey text-white"
                                        : ""
                            }
                        >
                            <td className="border-gray-300 border px-4 py-2">
                                <div className="flex flex-wrap justify-center gap-2">
                                    {currentUser?.type === "ADMIN" ? (
                                        <>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleUpdate(project.id)}
                                                className="mr-2 text-black"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDelete(project.id)}
                                                className="mr-2"
                                            >
                                                <Trash className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className={"text-black"}
                                                onClick={() =>
                                                    ProjectService.archiveProject(project.id).then(
                                                        () => loadProjects(),
                                                    )
                                                }
                                            >
                                                <Archive className="mr-2 h-4 w-4 text-black" />
                                                Arhivează
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleAssign(project.id)}
                                                disabled={isUserAssignedToProject(project)}
                                                className="mr-2"
                                            >
                                                <Plus className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleUnassign(project.id)}
                                                disabled={!isUserAssignedToProject(project)}
                                                className="mr-2"
                                            >
                                                <Minus className="h-4 w-4" />
                                            </Button>
                                            {isUserAssignedToProject(project) && (
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenProgressDialog(project);
                                                    }}
                                                >
                                                    <BarChart className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </td>

                            {currentUser?.type === "ADMIN" ? (
                                <>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.id + 100}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.name}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.description}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        <Link
                                            to={`/clients?editId=${project.clientId}`}
                                            className="hover:underline"
                                        >
                                            {getClientName(project.clientId)}
                                        </Link>
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.address}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {formatHoursAndMinutes(project.hours)}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.price}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.advance}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.invoice ? "Da" : "Nu"}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.status}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        <div className="bg-gray-200 h-2.5 w-full rounded-full">
                                            <div
                                                className="h-2.5 rounded-full bg-[#C51321]"
                                                style={{ width: `${project.progress || 0}%` }}
                                            ></div>
                                        </div>
                                        <span className="mt-1 block text-center text-xs">
                          {project.progress || 0}%
                        </span>
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.points || 0}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {new Date(project.startDate).toLocaleDateString(
                                            "ro-RO",
                                        )}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {new Date(project.limitDate).toLocaleDateString(
                                            "ro-RO",
                                        )}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.total}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.materials.length > 0
                                            ? project.materials.map((material, index) => (
                                                <div key={index}>
                                                    <div>
                                                        {getMaterialName(material.materialId)} |{" "}
                                                        {material.quantity},
                                                    </div>
                                                </div>
                                            ))
                                            : "Niciun material"}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.users.length > 0
                                            ? project.users.map((user, index) => (
                                                <div key={index}>
                                                    <div>[{user.name}]</div>
                                                </div>
                                            ))
                                            : "Niciun angajat"}
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.id + 100}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.name}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.description}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.status}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        <div className="bg-gray-200 h-2.5 w-full rounded-full">
                                            <div
                                                className="h-2.5 rounded-full bg-[#C51321]"
                                                style={{ width: `${project.progress || 0}%` }}
                                            ></div>
                                        </div>
                                        <span className="mt-1 block text-center text-xs">
                          {project.progress || 0}%
                        </span>
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.points || 0}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {new Date(project.startDate).toLocaleDateString(
                                            "ro-RO",
                                        )}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {new Date(project.limitDate).toLocaleDateString(
                                            "ro-RO",
                                        )}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.materials.length > 0
                                            ? project.materials.map((material, index) => (
                                                <div key={index}>
                                                    <div>
                                                        {getMaterialName(material.materialId)} |{" "}
                                                        {material.quantity},
                                                    </div>
                                                </div>
                                            ))
                                            : "Niciun material"}
                                    </td>
                                    <td className="border-gray-300 border px-4 py-2">
                                        {project.users.length > 0
                                            ? project.users.map((user, index) => (
                                                <div key={index}>
                                                    <div>[{user.name}]</div>
                                                </div>
                                            ))
                                            : "Niciun angajat"}
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="bg-white min-h-screen">
            {currentUser?.type === "ANGAJAT" ? <NavbarEmployee/> : <Navbar/>}
            <main className="w-full px-4 py-8 ml-0">
                <h1 className="text-2xl font-bold mb-6 text-center">Proiecte</h1>
                <Input
                    type="text"
                    placeholder="Caută proiect..."
                    className="w-full mb-6"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                {isSmallScreen
                    ? filteredProjects
                        .slice((currentPage - 1) * projectsPerPage, currentPage * projectsPerPage)
                        .map(renderProjectCard)
                    : renderProjectTable()}
                <div className="flex justify-center mt-6 space-x-2">
                    <Button
                        variant="outline"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        &lt;
                    </Button>
                    <span className="py-2">
            Pagină {currentPage} din {Math.ceil(filteredProjects.length / projectsPerPage)}
          </span>
                    <Button
                        variant="outline"
                        onClick={() =>
                            setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredProjects.length / projectsPerPage)))
                        }
                        disabled={currentPage === Math.ceil(filteredProjects.length / projectsPerPage)}
                    >
                        &gt;
                    </Button>
                </div>

                {selectedProject && (
                    <ProgressUpdateDialog
                        project={selectedProject}
                        isOpen={progressDialogOpen}
                        onClose={handleCloseProgressDialog}
                        onProgressUpdate={loadProjects}
                    />
                )}
            </main>
        </div>
    )
}

export default ProjectsPage

